import { AuthenticationModel, CompanyType, Right } from './../services/api-services';
import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, delay, finalize, map, switchMap } from 'rxjs/operators';
import { AuthenticationApiService } from './api-services';
import StorageHelper from '../helpers/storage.helper';
import { StringHelper } from '../helpers/string.helper';

@Injectable({
  providedIn: 'root'
})
// Stores and handles the authentication state of the user and all the tokens.
// It can also handle different users per browser over multiple tabs (allowMultipleUsers).
export class AuthenticationService {
  private readonly authenticationKey = 'Calario.authentication';
  private readonly isRefreshingTokenKey = 'Calario.isRefreshingToken';
  private readonly accessTokenPayloadKey = 'Calario.access_token_payload';
  private readonly authenticatedSubject$ = new ReplaySubject<AuthenticationModel | null>(1);
  public readonly authenticated$ = this.authenticatedSubject$.asObservable();
  private accessTokenPayload: any | null = null; // Used to store the decoded access token
  private static email: string | null = null; // Static property to store the e-mail of the user and allow external access (Logging)
  private readonly storage: Storage = localStorage;
  public readonly useHttpOnlyCookie: boolean = true;

  constructor(private readonly authenticationApiService: AuthenticationApiService) {
    this.setAuthentication(this.getRestoredAuthentication());
  }

  private getRestoredAuthentication(): AuthenticationModel | null {
    if (this.useHttpOnlyCookie) {
      const payload = StorageHelper.getItemAsObject<any | null>(this.storage, this.accessTokenPayloadKey);

      if (payload) {
        return {
          payload: payload
        } as AuthenticationModel;
      }
    }
    else {
      return this.getAuthenticationFromStorage();
    }

    return null;
  }

  public setAuthentication(authentication: AuthenticationModel | null): void {
    if (authentication) {
      if(this.useHttpOnlyCookie && authentication.payload) {
        this.accessTokenPayload = authentication.payload;

        StorageHelper.setItemFromObject<any | null>(this.storage, this.accessTokenPayloadKey, this.accessTokenPayload);
      }
      else if (authentication.access_token) {
        // Store the access token payload in memory so it must not be decoded again
        this.accessTokenPayload = this.decodeJwt(authentication.access_token);

        StorageHelper.setItemFromObject<any | null>(this.storage, this.accessTokenPayloadKey, this.accessTokenPayload);

        // Store access and refresh token
        StorageHelper.setItemFromObject<AuthenticationModel>(this.storage, this.authenticationKey, authentication);
      }
      else {
        this.removeAuthentication();
      }

      // Hold the e-mail in a static property to be able to access it from everywhere (logger)
      AuthenticationService.email = this.accessTokenPayload?.email;
    }
    else {
      this.removeAuthentication();
    }

    this.isRefreshingToken = false;
    this.authenticatedSubject$.next(authentication);
  }

  private removeAuthentication(): void {
    // Remove the items if data was null or undefined
    this.accessTokenPayload = null;

    StorageHelper.clear(this.storage, this.authenticationKey);
    StorageHelper.clear(this.storage, this.accessTokenPayloadKey);

    AuthenticationService.email = null;
  }

  public logout(): Observable<void> {
    const refreshToken = this.useHttpOnlyCookie ?
      null :
      this.getRefreshTokenFromStorage();

    if (refreshToken || this.useHttpOnlyCookie) {
      return this.authenticationApiService.logout(refreshToken ?? undefined)
        .pipe(catchError(error => {
          // Ignore errors from the API so we don't interrupt the logout processes
          console.error(error);
          return of(null);
        }))
        .pipe(map(() => {
          return this.setAuthentication(null);
        }));
    }
    else {
      return of(this.setAuthentication(null));
    }
  }

  public getLoginUrl(redirectUrl?: string): string {
    return `/authentication/login${(redirectUrl ? `?redirectUrl=${redirectUrl}` : '')}`;
  }

  public getAccessTokenFromStorage(): string | null {
    return this.getAuthenticationFromStorage()?.access_token ?? null;
  }

  public getRefreshTokenFromStorage(): string | null {
    return this.getAuthenticationFromStorage()?.refresh_token ?? null;
  }

  private checkTokenExpired(payload: any): boolean {
    const expiryDate = payload?.exp ?? 0;

    return Math.floor(new Date().getTime() / 1000) >= expiryDate;
  }

  public tokenExpired(): boolean {
    return this.checkTokenExpired(this.accessTokenPayload);
  }

  public getUserId(): string | null {
    return this.accessTokenPayload?.unique_name;
  }

  public getFirstName(): string | null {
    return this.accessTokenPayload?.given_name;
  }

  public getLastName(): string | null {
    return this.accessTokenPayload?.family_name;
  }

  public getHouseholdId(): string | null {
    return this.accessTokenPayload?.household_id;
  }

  public getFullName(): string | null {
    return `${this.getFirstName()} ${this.getLastName()}`;
  }

  public getRights(): Right[] {
    let rights = this.accessTokenPayload?.role;

    if (rights === null || rights === undefined) {
      return [];
    }

    if (!Array.isArray(rights)) {
      rights = [rights];
    }

    if (!rights.length) {
      return [];
    }

    return rights.map(right => right as Right);
  }

  public hasRight(right: Right): boolean {
    if (!this.isAuthenticated()) {
      return false;
    }

    return this.getRights().includes(right);
  }

  public hasAnyRight(rights: Right[]): boolean {
    if (!this.isAuthenticated()) {
      return false;
    }

    return this.getRights().some(right => rights.includes(right));
  }

  // Check if the user is logged in even if the token is expired
  public isAuthenticated(): boolean {
    return !!this.accessTokenPayload;
  }

  public getUserCompanyId = (): string | null => this.accessTokenPayload?.company_id;
  public getUserCompanyType = (): CompanyType | null => this.accessTokenPayload?.company_type ? CompanyType[<string>this.accessTokenPayload?.company_type] : null;

  public authenticate(userName: string, password: string): Observable<AuthenticationModel | null> {
    return this.authenticationApiService.token(userName, password)
      .pipe(map((loggedInModel: AuthenticationModel | null) => {
        this.setAuthentication(loggedInModel);
        return loggedInModel;
      }));
  }

  public refreshToken(refreshToken?: string | null): Observable<AuthenticationModel | null> {
    if (this.isRefreshingToken) {
      // When using multiple tabs with the same access token, 
      // only one tab should refresh the token and consume the refresh token.
      // Therefor wait here until the token is refreshed and reuse the new tokens from the first tab.
      return this.checkForRefreshedToken({ count: 1, max: 10, delayMilliseconds: 300 });
    }

    if (!this.useHttpOnlyCookie && !refreshToken) {
      refreshToken = this.getRefreshTokenFromStorage();
    }

    if (refreshToken || this.useHttpOnlyCookie) {
      this.isRefreshingToken = true;

      return this.authenticationApiService.refresh(refreshToken)
        .pipe(
          map((loggedInModel: AuthenticationModel | null) => {
            this.setAuthentication(loggedInModel);
            return loggedInModel;
          }),
          catchError((error, caught) => {
            this.setAuthentication(null);
            return of(null);
          }),
          finalize(() => this.isRefreshingToken = false));
    }
    else {
      return of(null)
    }
  }

  public static getEmail(): string | null {
    return AuthenticationService.email;
  }

  private decodeJwt(token: string): any {
    return JSON.parse(StringHelper.decodeBase64(token.split('.')[1]));
  }

  private getAuthenticationFromStorage(): AuthenticationModel | null {
    if (this.useHttpOnlyCookie) {
      throw new Error('This method should not be used when using HttpOnly cookies.');
    }

    return StorageHelper.getItemAsObject<AuthenticationModel>(this.storage, this.authenticationKey);
  };

  private checkForRefreshedToken(counter: { count: number, max: number, delayMilliseconds: number }): Observable<AuthenticationModel | null> {
    if (!this.isRefreshingToken) {
      console.log(`Other tab has refreshed the token.`);
      return of(this.getRestoredAuthentication());
    }
    else if (counter.count < counter.max) {
      const totalDelayMilliseconds = counter.delayMilliseconds * counter.count; // Increase delay linearly
      counter.count = counter.count + 1;
      console.log(`Other tab has not yet refreshed the token. Waiting ${totalDelayMilliseconds}ms...`);
      return of(null)
        .pipe(delay(totalDelayMilliseconds))
        .pipe(switchMap(() => this.checkForRefreshedToken(counter)));
    }

    console.log(`Other tab has NOT refreshed the token. Every tab will be signed out.`);

    return of(null);
  }

  private get isRefreshingToken(): boolean {
    return StorageHelper.getItemAsObject<boolean>(this.storage, this.isRefreshingTokenKey) ?? false;
  }

  private set isRefreshingToken(value: boolean) {
    if (!value) {
      StorageHelper.clear(this.storage, this.isRefreshingTokenKey);
      return;
    }

    StorageHelper.setItemFromObject<boolean>(this.storage, this.isRefreshingTokenKey, value);
  }
}
